/** Choose a sign-in method first; a clear consent step records the current terms before OAuth. */
import { useRef, useState } from 'react';
import { TERMS_VERSION, type SignInConsent } from '@orbit-mind/shared-types';
import { useLocale } from '@orbit-mind/engine-react';
import { Button } from '../ui/controls';
import { Modal } from '../ui/Modal';
import { offerInviteCode, signInHref, type AccountProvider } from './api';
import { ProviderIcon } from './ProviderIcon';
import { EmailPasswordForm } from './EmailPasswordForm';
import { ConsentStep } from './ConsentStep';

export interface SignInDialogProps {
  readonly passwordEnabled?: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly providers: readonly AccountProvider[];
  readonly returnTo: string;
  readonly inviteNeeded: boolean;
  readonly onInviteAccepted: () => void;
}
export function SignInDialog({ open, onOpenChange, providers, returnTo, inviteNeeded, onInviteAccepted, passwordEnabled }: SignInDialogProps): React.JSX.Element {
  const { text: t } = useLocale();
  const [existing, setExisting] = useState(false);
  const [code, setCode] = useState('');
  const [offering, setOffering] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(rememberedAgreement);
  const [news, setNews] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const pendingSubmit = useRef<((choice: SignInConsent) => void) | null>(null);
  const consent = { terms: TERMS_VERSION, news };
  const close = (next: boolean): void => {
    if (!next) { setPending(null); pendingSubmit.current = null; setNews(false); setEmailOpen(false); }
    onOpenChange(next);
  };
  const accept = (): void => {
    rememberAgreement(); setAgreed(true); setPending(null);
    const submit = pendingSubmit.current;
    pendingSubmit.current = null;
    submit?.(consent);
  };
  if (inviteNeeded && !existing) return (
    <Modal open={open} onOpenChange={close} size="narrow" title={t('Orbit Mind is in closed beta', 'Orbit Mind está em beta fechado')} description={t('An invite code is needed to create an account.', 'É necessário um convite para criar uma conta.')}>
      <form className="grid gap-3 p-5" onSubmit={(event) => {
        event.preventDefault(); if (!code.trim() || offering) return;
        setOffering(true); setRefusal(null);
        void offerInviteCode(code.trim()).then((result) => { setOffering(false); if (result.ok) onInviteAccepted(); else setRefusal(result.error.message); });
      }}>
        <label htmlFor="invite-code">{t('Invite code', 'Código de convite')}</label>
        <input id="invite-code" data-invite-code value={code} onChange={(event) => { setCode(event.target.value); }} autoComplete="off" spellCheck={false} className="border-edge bg-surface text-ink h-11 rounded-lg border px-3" />
        {refusal && <p role="alert">{refusal}</p>}
        <p className="text-ink-muted text-sm">{t('Already have an account? You do not need a new invite.', 'Já tem uma conta? Você não precisa de outro convite.')}</p>
        <Button onClick={() => { setExisting(true); }}>{t('I already have an account', 'Já tenho uma conta')}</Button>
        <Button tone="primary" size="lg" type="submit" disabled={offering || !code.trim()}>{offering ? t('Checking…', 'Verificando…') : t('Use this code', 'Usar este código')}</Button>
      </form>
    </Modal>
  );
  return (
    <Modal open={open} onOpenChange={close} size="narrow" title={t('Orbit Mind account', 'Conta Orbit Mind')} description={t('Sign in to keep your maps in step on every device.', 'Entre para sincronizar seus mapas entre dispositivos.')}>
      <div className="grid gap-5 p-5 sm:p-6">
        {pending !== null && <ConsentStep news={news} onNewsChange={setNews} onBack={() => { setPending(null); pendingSubmit.current = null; }} onAccept={accept}
          {...(pending !== 'email-register' ? { continueHref: signInHref(pending, returnTo, consent) } : {})} />}
        <div hidden={pending !== null} className={pending !== null ? 'hidden' : 'grid gap-5'}>
          <p className="text-accent text-sm font-medium">{t('Your ideas, connected.', 'Suas ideias, conectadas.')}</p>
          <div className="grid gap-3">
            {providers.map((provider) => agreed ? (
              <a key={provider.id} href={signInHref(provider.id, returnTo, consent)} className={PROVIDER_BUTTON}>
                <span className={PROVIDER_CHIP}><ProviderIcon provider={provider.id} /></span>
                <span className="flex-1 pr-9 text-center">{t('Continue with', 'Continuar com')} {provider.label}</span>
              </a>
            ) : (
              <button key={provider.id} type="button" onClick={() => { setPending(provider.id); }} className={PROVIDER_BUTTON}>
                <span className={PROVIDER_CHIP}><ProviderIcon provider={provider.id} /></span>
                <span className="flex-1 pr-9 text-center">{t('Continue with', 'Continuar com')} {provider.label}</span>
              </button>
            ))}
            {passwordEnabled === true && !emailOpen && <Button size="lg" onClick={() => { setEmailOpen(true); }}>{t('Continue with email', 'Continuar com e-mail')}</Button>}
          </div>
          {passwordEnabled === true && emailOpen && <EmailPasswordForm agreed={agreed} consent={consent}
            requestConsent={(submit) => { pendingSubmit.current = submit; setPending('email-register'); }}
            onSignedIn={() => { close(false); onInviteAccepted(); }} />}
          <button type="button" onClick={() => { close(false); }} className="text-ink-muted hover:text-ink justify-self-center text-sm font-medium hover:underline">{t('Continue without an account', 'Continuar sem conta')}</button>
          <p className="text-ink-muted text-xs leading-relaxed">{t('Guest maps stay in a separate library. Sign in to sync your account maps.', 'Os mapas de visitante ficam em uma biblioteca separada. Entre para sincronizar os mapas da sua conta.')}</p>
        </div>
      </div>
    </Modal>
  );
}
const PROVIDER_BUTTON = 'border border-edge bg-surface text-ink flex min-h-12 items-center gap-3 rounded-xl px-1.5 text-[0.9375rem] font-semibold shadow-sm transition-[filter,transform] hover:brightness-110 active:scale-[0.99]';
const PROVIDER_CHIP = 'flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#1f1b2e] shadow-sm';
const AGREEMENT_KEY = 'orbit-mind.terms-agreed';
function rememberedAgreement(): boolean { try { return window.localStorage.getItem(AGREEMENT_KEY) === TERMS_VERSION; } catch { return false; } }
function rememberAgreement(): void { try { window.localStorage.setItem(AGREEMENT_KEY, TERMS_VERSION); } catch { /* Acceptance still travels in the request. */ } }
